// File: frontend/src/context/ConnectivityContext.tsx

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { setNetworkStatusCallbacks } from "../services/apiClient";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

/** How often the app re-checks the server while it believes it is offline. */
const HEARTBEAT_MS = 15000;

/** Heartbeat requests are cheap but must not hang forever on a dead link. */
const HEARTBEAT_TIMEOUT_MS = 6000;

interface ConnectivityState {
  /** False while the app cannot reach the backend. */
  isOnline: boolean;
  /** True for a few seconds right after the connection came back. */
  justReconnected: boolean;
  /** Forces an immediate reachability check (used by the retry button). */
  checkNow: () => void;
}

const ConnectivityContext = createContext<ConnectivityState>({
  isOnline: true,
  justReconnected: false,
  checkNow: () => {},
});

export const useConnectivity = (): ConnectivityState =>
  useContext(ConnectivityContext);

/**
 * Tracks whether the app can actually talk to the backend.
 *
 * Two independent signals are combined because neither is sufficient alone:
 *
 *  1. window online/offline events — instant, but they only describe the
 *     network interface. A phone connected to a captive-portal wifi reports
 *     itself as fully online while nothing can reach the internet.
 *  2. Real request failures reported by apiClient — authoritative, but only
 *     observed when the user happens to trigger a request.
 *
 * While offline, a lightweight heartbeat polls the backend so the banner can
 * disappear on its own once the connection returns.
 */
export const ConnectivityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [justReconnected, setJustReconnected] = useState(false);

  // Avoids re-rendering on every successful request — only transitions matter
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;

  const goOffline = useCallback(() => {
    if (!isOnlineRef.current) return;
    isOnlineRef.current = false;
    setIsOnline(false);
    setJustReconnected(false);
  }, []);

  const goOnline = useCallback(() => {
    if (isOnlineRef.current) return;
    isOnlineRef.current = true;
    setIsOnline(true);
    setJustReconnected(true);
  }, []);

  /** One reachability probe against the backend health endpoint. */
  const probe = useCallback(async () => {
    const controller = new AbortController();
    const timer = window.setTimeout(
      () => controller.abort(),
      HEARTBEAT_TIMEOUT_MS
    );
    try {
      // cache: no-store so a cached 200 can never mask a dead connection
      const res = await fetch(`${API_BASE_URL}/api/health`, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
      });
      if (res.ok) goOnline();
    } catch {
      goOffline();
    } finally {
      window.clearTimeout(timer);
    }
  }, [goOnline, goOffline]);

  // Wire apiClient's real request outcomes into this state
  useEffect(() => {
    setNetworkStatusCallbacks(goOffline, goOnline);
    return () => setNetworkStatusCallbacks(null, null);
  }, [goOffline, goOnline]);

  // Browser-level events — fast but not trustworthy on their own
  useEffect(() => {
    const handleOffline = () => goOffline();
    // "online" only means the interface is up; confirm with a real request
    const handleOnline = () => probe();

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [goOffline, probe]);

  // Heartbeat runs only while offline — no idle traffic on a healthy session
  useEffect(() => {
    if (isOnline) return;
    const id = window.setInterval(probe, HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [isOnline, probe]);

  // The "connection is back" confirmation is transient
  useEffect(() => {
    if (!justReconnected) return;
    const id = window.setTimeout(() => setJustReconnected(false), 3000);
    return () => window.clearTimeout(id);
  }, [justReconnected]);

  return (
    <ConnectivityContext.Provider
      value={{ isOnline, justReconnected, checkNow: probe }}
    >
      {children}
    </ConnectivityContext.Provider>
  );
};